// @ts-nocheck
import InfoIcon from '@mui/icons-material/Info';
import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import moment from 'moment';

import {
  FAntdTimePicker,
  FHButton,
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHConfirmDialog,
  FHSelect,
  FHTooltip,
  FHWarning,
} from '../../../../components/base';
import ClientTimezoneDisplay from '../../../../components/layout/ClientTimezoneDisplay';
import TrashIcon from '../../../../components/svgs/TrashIcon3';
import { BcbaAttendance, Roles } from '../../../../constants';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  combineMomentDateTime,
  disabledDatesOutsideRange,
  disabledFutureHoursAndMinutes,
} from '../../../../helpers';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import { DeliveryMethods, Durations, Locations, newLocations } from '../constants';

import type { ChangeEvent, HTMLAttributes } from 'react';
import type { Moment } from 'moment';
import type { IOption, SelectValueType } from '../../../../utils/types';
import type { IExistingSessionInfoFieldsProps } from '../types';

export default function ExistingSessionInfoFields({
  abbreviatedZone,
  bay6BasedGridSize,
  bcbaAttendanceStatus,
  clientOptions,
  cohortAssignmentLoading,
  currentClientCohortAssignments,
  deliveryMethod,
  deliveryMethodOptions,
  disableByClientBay,
  durationLabels,
  durationToolTipOpen,
  endBetween10to12,
  handleBcbaAttendanceChange,
  handleClientChange,
  handleDeleteSession,
  handleDeliveryMethodChange,
  handleGetOutOnRangeCount,
  handleStartSession,
  handleTimeRangeUpdate,
  handleTimeSelect,
  handleUpdateLocation,
  handleUpdateSessionTime,
  isBay6Active,
  isEditable,
  isTZIdentified,
  location,
  narrativeSuggestionLoading,
  selectedClientDetails,
  selectedClientId,
  selectedDuration,
  session,
  sessionLoading,
  sessionStartDate,
  setOpenClientConfirm,
  setPendingSelectedClientId,
  setShowConfirmDeleteDialog,
  setShowDurationEventWarning,
  setShowRateEventWarning,
  showBcbaAttendanceField,
  showConfirmDeleteDialog,
  showDeleteButton,
  showDurationEventWarning,
  showRateEventWarning,
  startBetween10to12,
  startSessionLoading,
  timeStartValue,
  timeToolTipOpen,
  tooltipTitle,
  user,
  zone,
}: IExistingSessionInfoFieldsProps) {
  return (
    <Box
      sx={{
        textAlign: 'left',
        backgroundColor: FORTA_BLUE.light3,
        border: `1px solid ${FORTA_BLUE.light2}`,
        p: 2,
        borderRadius: '8px',
      }}
    >
      <Grid container spacing={{ xs: 1, sm: 2, md: 1, lg: 2 }}>
        <Grid item xs={12} sm={6} md={3} lg={2.5}>
          <Box display={'flex'} flexDirection={'column'} mr={1} mb={2}>
            <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
              Client name
            </Typography>
            <FHAutocomplete
              data-testid="clientNameDropDownSessionNotes"
              fullWidth
              inputHeight={40}
              sx={{ boxShadow: 'none' }}
              placeholder="Select client"
              clearIcon={false}
              isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                return (
                  <Box
                    {...props}
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                    key={option.id}
                    data-testid={`clientLabel${option.id}`}
                  >
                    <Typography>{option.label}</Typography>
                    {option.description && (
                      <Typography
                        sx={{
                          px: 1,
                          ml: 1,
                          borderRadius: '4px',
                          color: FORTA_WHITE,
                          fontWeight: 700,
                          backgroundColor: FORTA_BLUE.light1,
                        }}
                      >
                        {capitalizeFirstLetter(option.description)}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              disabled={!!session || narrativeSuggestionLoading}
              options={clientOptions}
              optionDataTestIdPrefix="clientNameItemSessionNotes"
              value={
                clientOptions.length > 0 && selectedClientId
                  ? (clientOptions.find((cl) => cl.id === selectedClientId) ?? null)
                  : null
              }
              onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                void event;
                const clientId = newValue?.id == null ? null : Number(newValue.id);
                setPendingSelectedClientId(clientId);
                if (selectedClientId) {
                  setOpenClientConfirm(true);
                } else {
                  handleClientChange(clientId);
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.25}>
          <Box display={'flex'} flexDirection={'column'} mr={1} mb={2}>
            <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }}>
              Start date
            </Typography>
            <FHAntdDateTimePicker
              data-testid="sessionNoteDatePicker"
              sx={{
                width: '100%',
                boxShadow: 'none',
              }}
              format="MM/DD/YY"
              disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -14, 0)}
              disabledTime={(current: Moment) => disabledFutureHoursAndMinutes(current)}
              showNow={true}
              allowClear={false}
              disabled={(session && !isEditable) || narrativeSuggestionLoading || !selectedClientId}
              use12Hours
              inputHeight={40}
              value={sessionStartDate ? moment(sessionStartDate) : null}
              onBlur={(e: any) => {
                const newVal = timeStartValue
                  ? combineMomentDateTime(moment(e.target.value), moment(timeStartValue))
                  : moment(e.target.value);
                handleUpdateSessionTime(moment(newVal, 'MM/DD/YYYY - hh:mm A'));
              }}
              onOk={(value: Moment) => {
                const newVal = timeStartValue
                  ? combineMomentDateTime(moment(value), moment(timeStartValue))
                  : moment(value);
                handleUpdateSessionTime(moment(newVal));
              }}
              onChange={(value: Moment) => {
                const outsideRageCount = handleGetOutOnRangeCount(moment(value));
                if (outsideRageCount !== null && outsideRageCount > 0) {
                  setShowRateEventWarning(true);
                  return;
                }
                handleTimeRangeUpdate('date_start', value);
              }}
              onSelect={(value: Moment) => {
                const outsideRageCount = handleGetOutOnRangeCount(moment(value));
                if (outsideRageCount !== null && outsideRageCount > 0) {
                  setShowRateEventWarning(true);
                  return;
                }
                handleTimeRangeUpdate('date_start', value);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.25}>
          <Box mr={1}>
            <Typography fontWeight="bold" textAlign="left" mb={1}>
              Start time
            </Typography>
            <FHTooltip
              sx={{ inset: 'auto auto -6px 0px', opacity: 0.9, mb: 10 }}
              title={tooltipTitle}
              arrow
              open={timeToolTipOpen || startBetween10to12}
              placement="top"
              maxwidth={340}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_WHITE}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <span>
                <FAntdTimePicker
                  data-testid="sessionNoteTimePicker"
                  inputHeight={40}
                  style={{ width: '100%' }}
                  format="h:mm A"
                  allowClear={false}
                  showNow={true}
                  coloredBackground={false}
                  use12Hours
                  disabled={
                    (session && !isEditable) || narrativeSuggestionLoading || !sessionStartDate
                  }
                  disabledTime={() =>
                    disabledFutureHoursAndMinutes(
                      combineMomentDateTime(
                        moment(sessionStartDate),
                        moment(timeStartValue ?? undefined),
                      ),
                    )
                  }
                  value={timeStartValue ? moment(timeStartValue) : null}
                  onBlur={() => {
                    const newVal = timeStartValue
                      ? combineMomentDateTime(moment(sessionStartDate), moment(timeStartValue))
                      : moment(sessionStartDate);
                    handleUpdateSessionTime(newVal);
                  }}
                  onOk={(newValue: Moment) => {
                    handleUpdateSessionTime(
                      timeStartValue
                        ? combineMomentDateTime(moment(newValue), moment(timeStartValue))
                        : moment(newValue),
                    );
                  }}
                  onChange={handleTimeSelect}
                  onSelect={handleTimeSelect}
                  error={timeToolTipOpen || startBetween10to12}
                />
              </span>
            </FHTooltip>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.25}>
          <Box width={'100%'}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography fontWeight="bold" textAlign="left" mb={1}>
                Client time
              </Typography>
              <FHTooltip
                sx={{ inset: 'auto auto -6px 0px' }}
                title={
                  selectedClientId
                    ? `Client's timezone is determined by their zip code ${
                        selectedClientDetails
                          ? `(Client zip code: ${selectedClientDetails.street_address_zip})`
                          : ''
                      }`
                    : 'Select a client to determine their timezone'
                }
                arrow
                placement="top"
                maxwidth={340}
                tooltipBorderRadius="8px"
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
              >
                <Box sx={{ ml: 0.75 }}>
                  <InfoIcon
                    sx={{
                      color: FORTA_BLUE.primary,
                      width: 21,
                      height: 21,
                    }}
                  />
                </Box>
              </FHTooltip>
            </Box>
            <ClientTimezoneDisplay
              dateTime={
                timeStartValue
                  ? isTZIdentified
                    ? combineMomentDateTime(moment(sessionStartDate), moment(timeStartValue))
                    : null
                  : null
              }
              sx={{ backgroundColor: FORTA_WHITE }}
              zone={zone ?? ''}
              message={
                selectedClientId
                  ? selectedClientId && !isTZIdentified
                    ? 'Timezone undetermined'
                    : 'Select date & time first'
                  : 'Select a client first'
              }
              abbreviatedZone={abbreviatedZone ?? ''}
              selectedClientId={isTZIdentified ? selectedClientId : null}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={2.25}>
          <Box display={'flex'} flexDirection={'column'} mb={2}>
            <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }}>
              Duration
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              <FHTooltip
                sx={{ inset: 'auto auto -6px 0px', opacity: 0.9 }}
                title={tooltipTitle}
                arrow
                open={durationToolTipOpen || endBetween10to12}
                placement="top"
                maxwidth={340}
                tooltipBorderRadius="8px"
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
              >
                <Box sx={{ flexGrow: 1 }}>
                  <FHSelect
                    data-testid="durationDropDownSessionNotes"
                    inputHeight={40}
                    sx={{ flexGrow: 1, width: '100%', boxShadow: 'none' }}
                    disabled={(Boolean(session) && !isEditable) || narrativeSuggestionLoading}
                    options={Object.entries(Durations).map(([label, value]) => ({ value, label }))}
                    optionDataTestIdPrefix="durationItemSessionNotes"
                    value={selectedDuration}
                    renderValue={(value) => durationLabels[String(value)]}
                    onUpdate={(value: SelectValueType) =>
                      handleTimeRangeUpdate('expected_duration', value)
                    }
                    error={durationToolTipOpen || endBetween10to12}
                  />
                </Box>
              </FHTooltip>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
        {showBcbaAttendanceField && (
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" flexDirection={'column'} mr={1} mb={2}>
              <Typography
                fontWeight="bold"
                textAlign="left"
                noWrap
                alignSelf="flex-start"
                sx={{ mb: 1 }}
              >
                {!isBay6Active
                  ? 'BCBA Attendance (Partial/Full)'
                  : 'Supervising BCBA Attendance (Partial/Full)'}
              </Typography>
              <FHSelect
                data-testid="bcbaAttendanceDropDownSessionNotes"
                fullWidth
                inputHeight={40}
                sx={{ boxShadow: 'none' }}
                disabled={(Boolean(session) && !isEditable) || narrativeSuggestionLoading}
                options={Object.values(BcbaAttendance).map((value) => ({ value, label: value }))}
                optionDataTestIdPrefix="bcbaAttendanceItemSessionNotes"
                value={
                  bcbaAttendanceStatus === true
                    ? BcbaAttendance.Present
                    : bcbaAttendanceStatus === false
                      ? BcbaAttendance.Absent
                      : ''
                }
                onUpdate={(value: SelectValueType) => handleBcbaAttendanceChange(String(value))}
              />
              {areUserRolesIn(user?.roles ?? [], [Roles.Bcba]) && (
                <FHWarning
                  bold
                  sxText={{ ml: 1, fontSize: '14px' }}
                  sxContainer={{ mt: 1, mb: -2, backgroundColor: 'transparent', p: 0 }}
                  content='Select "Yes" if a different BCBA was present to supervise this session'
                />
              )}
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={bay6BasedGridSize}>
          <Box display={'flex'} flexDirection={'column'} mr={1} mb={2}>
            <Typography
              fontWeight="bold"
              noWrap
              textAlign="left"
              alignSelf={'flex-start'}
              sx={{ mb: 1 }}
            >
              Client location
            </Typography>
            <FHSelect
              data-testid="locationDropDownSessionNotes"
              fullWidth
              inputHeight={40}
              sx={{ boxShadow: 'none' }}
              disabled={(Boolean(session) && !isEditable) || narrativeSuggestionLoading}
              options={Object.values(newLocations).map((value) => ({ value, label: value }))}
              renderValue={(value) => String(value)}
              optionDataTestIdPrefix="locationItemSessionNotes"
              value={location}
              onUpdate={(value: SelectValueType) => {
                const locationValue = Object.values(Locations).find((item) => item === value);
                if (locationValue) handleUpdateLocation(locationValue);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={bay6BasedGridSize}>
          <Box display={'flex'} flexDirection={'column'} mr={1} mb={2}>
            <Typography
              fontWeight="bold"
              noWrap
              textAlign="left"
              alignSelf={'flex-start'}
              sx={{ mb: 1 }}
            >
              Delivery method
            </Typography>
            <FHSelect
              data-testid="deliveryMethodDropDownSessionNotes"
              fullWidth
              sx={{ boxShadow: 'none' }}
              inputHeight={40}
              disabled={(Boolean(session) && !isEditable) || narrativeSuggestionLoading}
              options={deliveryMethodOptions}
              optionDataTestIdPrefix="deliveryMethodItemSessionNotes"
              value={deliveryMethod}
              onUpdate={(value: SelectValueType) => {
                const deliveryMethodValue = Object.values(DeliveryMethods).find(
                  (item) => item === value,
                );
                if (deliveryMethodValue) handleDeliveryMethodChange(deliveryMethodValue);
              }}
            />
          </Box>
        </Grid>

        {user?.roles && !sessionLoading && (
          <Grid item xs={12} sm={6} md={4} lg={4.5}>
            {areUserRolesIn(user.roles, [Roles.Prbt, Roles.Vbt, Roles.Bcba]) && !session && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FHButton
                    data-testid="startSessionNotes"
                    sx={{
                      width: '100px',
                      height: 40,
                      fontSize: 16,
                      mt: 4,
                    }}
                    loading={startSessionLoading}
                    disabled={
                      timeToolTipOpen ||
                      durationToolTipOpen ||
                      cohortAssignmentLoading ||
                      disableByClientBay
                    }
                    onClick={handleStartSession}
                  >
                    Start
                  </FHButton>
                </Box>
                {areUserRolesIn(user.roles, [Roles.Bcba]) &&
                  currentClientCohortAssignments &&
                  !cohortAssignmentLoading &&
                  selectedClientId &&
                  disableByClientBay && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <FHWarning
                        bold
                        iconColor={FORTA_ERROR}
                        sxText={{ ml: 1, fontSize: '14px' }}
                        sxContainer={{ mt: 1, mb: -2, backgroundColor: 'transparent', p: 0 }}
                        content={`BCBAs are not allowed to create sessions for ${currentClientCohortAssignments.cohort?.name} clients.`}
                      />
                    </Box>
                  )}
                {cohortAssignmentLoading && <CircularProgress sx={{ mt: 1, mr: 5 }} size={16} />}
              </>
            )}
            {showDeleteButton && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  sx={{
                    p: 1,
                    ml: 5,
                    mb: 2,
                    mt: 4,
                    fontSize: 16,
                    borderRadius: '8px',
                    color: FORTA_ERROR,
                    border: `1px solid ${FORTA_ERROR}`,
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => setShowConfirmDeleteDialog(true)}
                  data-testid="deleteNoteButton"
                >
                  <TrashIcon color={FORTA_ERROR} width={18} height={18} />
                  <Typography ml={1}>Delete</Typography>
                </IconButton>
              </Box>
            )}
          </Grid>
        )}
      </Grid>
      <FHConfirmDialog
        open={showConfirmDeleteDialog}
        title={'Delete session'}
        content={'Are you sure you want to delete this session? This action cannot be undone.'}
        submittext={'Yes, delete'}
        canceltext={'Cancel'}
        submitBtnTestId={'deleteBtn'}
        cancelBtnTestId={'cancelBtn'}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteSession}
      />
      <FHConfirmDialog
        open={showRateEventWarning}
        title={'Events outside the session time'}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          maxHeight: '85vh',
          height: 'auto',
          width: '650px',
          maxWidth: '650px',
        }}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={4}>
            <Typography textAlign="center" fontSize={20} fontWeight={500}>
              There are existing events that fall outside the updated <br /> start date and
              duration.
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={500} mt={2} mb={1}>
              Please delete or adjust these events before <br /> proceeding with the update.
            </Typography>
          </Box>
        )}
        submittext={'Okay'}
        hideCancelButton
        onClose={() => setShowRateEventWarning(false)}
        onSubmit={() => {
          setShowRateEventWarning(false);
        }}
      />
      <FHConfirmDialog
        open={showDurationEventWarning}
        title={'Duration events outside the session time'}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        dialogStyles={{
          maxHeight: '85vh',
          height: 'auto',
          width: '650px',
          maxWidth: '650px',
        }}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={4}>
            <Typography textAlign="center" fontSize={20} fontWeight={500}>
              There are existing duration events that fall outside the updated session duration.
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={500} mt={2} mb={1}>
              Please delete or adjust these events before <br /> proceeding with the update.
            </Typography>
          </Box>
        )}
        submittext={'Okay'}
        hideCancelButton
        onClose={() => setShowDurationEventWarning(false)}
        onSubmit={() => {
          setShowDurationEventWarning(false);
        }}
      />
    </Box>
  );
}
